import React, { createContext, useEffect } from 'react';
import { getSubdomain } from '../hooks/useTenant';
import useTenantStore from '../store/useTenantStore';
import useAuthStore from '../store/useAuthStore';

export const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
  const { fetchTenantSettings, loading: loadingTenant, tenantSettings } = useTenantStore();
  const { isAuthenticated, tenant: authTenant, fetchMe } = useAuthStore();

  useEffect(() => {
    const initializeApp = async () => {
      let slugToFetch = getSubdomain();

      // 1. Si hay token, refrescamos la info del usuario (trae tenant y acl)
      if (isAuthenticated) {
        try {
          await fetchMe();
          // Priorizamos el slug que viene en el perfil del usuario autenticado
          const currentAuthState = useAuthStore.getState();
          if (currentAuthState.tenant?.slug) {
            slugToFetch = currentAuthState.tenant.slug;
          }
        } catch (error) {
          console.error("Error al refrescar sesión:", error);
        }
      }

      // 2. Obtenemos las configuraciones específicas (features y branding) usando el slug
      const host = typeof window !== 'undefined' ? window.location.hostname : '';
      const isLocal = host === 'localhost' || host === '127.0.0.1';
      const isMainDashboard = host === 'dashboard.legalcheck.com.co';

      if (isLocal || isMainDashboard) {
        // No intentamos obtener settings por tenant en local o en el dashboard principal
        const root = document.documentElement;
        root.style.setProperty('--color-primary', '#f97316');
        root.style.setProperty('--color-secondary', '#0f172a');
      } else if (slugToFetch && slugToFetch !== 'default') {
        try {
          await fetchTenantSettings(slugToFetch);
        } catch (error) {
          console.error("Error al cargar settings del tenant:", error);
        }
      } else {
        // Configuraciones por defecto si no hay subdominio (portal público)
        const root = document.documentElement;
        root.style.setProperty('--color-primary', '#f97316');
        root.style.setProperty('--color-secondary', '#0f172a');
      }
      
      // 3. Aplicar colores siempre basados en los settings cargados
      const finalSettings = useTenantStore.getState().tenantSettings;
      if (finalSettings) {
        const root = document.documentElement;
        const b = finalSettings.branding || finalSettings.settings || {};
        const p = b.primary_color || finalSettings.primary_color;
        const s = b.secondary_color || finalSettings.secondary_color;
        if (p) root.style.setProperty('--color-primary', p);
        if (s) root.style.setProperty('--color-secondary', s);
      }
    };

    initializeApp();
  }, [fetchMe, fetchTenantSettings, isAuthenticated]);

  // Bloqueamos la App hasta que tengamos la info mínima necesaria
  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"></div>
          <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Cargando Entorno...</span>
        </div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={tenantSettings}>
      {children}
    </TenantContext.Provider>
  );
};
